import { useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useClients } from '../../hooks/useClients';
import { useActors, useLinkActorClient } from '../../hooks/useActors';
import { ApiError } from '../../lib/api';
import { checkActorConflicts, type ActorConflictCheck } from '../../lib/actorsApi';
import { Select } from '../ui/Select';
import { usePermissions } from '../../hooks/usePermissions';
import { ClientIndustryPicker } from './ClientIndustryPicker';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Link an actor to a client, checked every time: from the actor's page (pick
 * the client) or from the client's „Actori” tab (pick the actor). Before
 * saving, the conflict check runs; when the actor already appeared at a
 * competitor (same industry) the alert names them and the link is only saved
 * with „Asociază oricum”, which records who confirmed it.
 */
export function LinkClientDialog({
  actor,
  client,
  onClose,
}: {
  /** From the actor's page. */
  actor?: { id: string; fullName: string };
  /** From the client's tab. */
  client?: { id: string; name: string };
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [clientId, setClientId] = useState(client?.id ?? '');
  const [actorId, setActorId] = useState(actor?.id ?? '');
  const [usedAt, setUsedAt] = useState(today());
  const [note, setNote] = useState('');
  const [check, setCheck] = useState<ActorConflictCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const link = useLinkActorClient();
  const { can } = usePermissions();
  const canSetIndustry = can('clients', 'canEdit');

  const { data: clients } = useClients({ active: 'true', sort: 'name', limit: 100 });
  const { data: actors } = useActors({}, !actor);

  const clientOptions = (clients?.data ?? []).map((c) => ({
    value: c.id,
    label: c.name,
    description: c.industry?.name ?? undefined,
  }));
  const actorOptions = (actors ?? [])
    .filter((a) => !client || !a.clients.some((l) => l.clientId === client.id))
    .map((a) => ({ value: a.id, label: a.fullName }));

  async function save(acknowledgeConflict: boolean) {
    await link.mutateAsync({
      actorId,
      clientId,
      usedAt,
      note: note.trim() || undefined,
      acknowledgeConflict,
    });
    onClose();
  }

  async function handleCheck() {
    setError(null);
    if (!actorId || !clientId) return;
    setChecking(true);
    try {
      const result = await checkActorConflicts(actorId, clientId);
      if (result.status === 'conflict') {
        setCheck(result);
        return;
      }
      await save(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ACTOR_CLIENT_EXISTS') {
        setError(t('actors.link.alreadyLinked'));
      } else {
        setError(err instanceof Error ? err.message : 'A apărut o eroare.');
      }
    } finally {
      setChecking(false);
    }
  }

  async function handleConfirm() {
    setError(null);
    try {
      await save(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A apărut o eroare.');
    }
  }

  const selectedClient = (clients?.data ?? []).find((c) => c.id === clientId);
  const noIndustry = Boolean(selectedClient && !selectedClient.industry);
  const competitors = check ? [...new Set(check.conflicts.map((c) => c.clientName))] : [];
  const title = actor
    ? t('actors.link.title', { name: actor.fullName })
    : t('actors.link.titleForClient', { client: client?.name ?? '' });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        {check ? (
          <div className="space-y-4 px-5 py-4" role="alert">
            <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {t('actors.link.conflictTitle')}
                </p>
                <p className="mt-1 text-sm text-amber-900">
                  {t('actors.conflictMessage', { clients: competitors.join(', ') })}
                </p>
              </div>
            </div>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {check.conflicts.map((c) => (
                <li
                  key={c.clientId}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-gray-900">{c.clientName}</span>
                  <span className="text-xs text-gray-500">
                    {c.industryName} · {t('actors.link.conflictLastShoot', { date: c.usedAt })}
                  </span>
                </li>
              ))}
            </ul>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('actors.link.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirm()}
                disabled={link.isPending}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {t('actors.link.confirm')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 px-5 py-4">
            {!client && (
              <Select
                label={t('actors.link.client')}
                value={clientId}
                onChange={setClientId}
                searchable
                options={clientOptions}
                placeholder={t('actors.link.client')}
              />
            )}
            {!actor && (
              <Select
                label={t('actors.link.actor')}
                value={actorId}
                onChange={setActorId}
                searchable
                options={actorOptions}
                placeholder={t('actors.link.actor')}
              />
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="ui-field-label">{t('actors.link.usedAt')}</span>
                <input
                  type="date"
                  value={usedAt}
                  onChange={(e) => setUsedAt(e.target.value)}
                  className="ui-input w-full"
                />
              </label>
              <label className="block">
                <span className="ui-field-label">{t('actors.link.note')}</span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('actors.link.notePlaceholder')}
                  className="ui-input w-full"
                />
              </label>
            </div>
            {noIndustry && selectedClient && (
              <div className="space-y-2 rounded-md bg-gray-50 p-3">
                <p className="flex gap-2 text-xs text-gray-600">
                  <Info className="h-4 w-4 shrink-0" />
                  {canSetIndustry
                    ? t('actors.link.unknownIndustryPick')
                    : t('actors.link.unknownIndustry')}
                </p>
                {canSetIndustry && <ClientIndustryPicker clientId={selectedClient.id} />}
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('actors.link.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleCheck()}
                disabled={!actorId || !clientId || checking || link.isPending}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {checking ? t('actors.link.checking') : t('actors.link.check')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
